// src/utils/timelineLayout.js
// Pure helpers for the by-room doctor availability timeline (DoctorDayTimeline).
// Side-effect free; operate on the override-aware resolved schedule already loaded
// by QueueManage (branchScheduleMembers). The resolver is called from=to=<day>, so
// each member carries exactly one day entry (days[0]).
//
// Shapes:
//   member = { branchMemberId, days: [{ date, slots:[{start,end,roomId}], overrideMarkers:[] }] }
//   doctor = { branchMemberId, name, color }
//   axis   = { startMin, endMin }            // minutes from midnight
//   row    = { branchMemberId, name, color, isLeaveWhole, startMin, ranges, rooms, segments }
//   segment= { leftPct, widthPct, start, end, roomId }
// Time strings may be "HH:MM" or "HH:MM:SS".

const DEFAULT_OPEN_MIN = 9 * 60;
const DEFAULT_CLOSE_MIN = 18 * 60;

export function parseTimeToMinutes(t) {
  if (t == null) return null;
  const parts = String(t).split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1] || 0);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

export function minutesToLabel(min) {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function dayOf(member) {
  return (member && member.days && member.days[0]) || null;
}

function slotsOf(member) {
  const day = dayOf(member);
  return (day && day.slots) || [];
}

export function buildTimeAxis(members, { openingTime, closingTime } = {}) {
  let start = parseTimeToMinutes(openingTime);
  let end = parseTimeToMinutes(closingTime);
  if (start == null) start = DEFAULT_OPEN_MIN;
  if (end == null) end = DEFAULT_CLOSE_MIN;
  (members || []).forEach(m => {
    slotsOf(m).forEach(s => {
      const ss = parseTimeToMinutes(s.start);
      const se = parseTimeToMinutes(s.end);
      if (ss != null && ss < start) start = ss;
      if (se != null && se > end) end = se;
    });
  });
  if (end <= start) end = start + 60; // never zero-width
  return { startMin: start, endMin: end };
}

export function timeToPct(min, axis) {
  const span = axis.endMin - axis.startMin;
  if (min == null || span <= 0) return 0;
  const pct = ((min - axis.startMin) / span) * 100;
  return Math.max(0, Math.min(100, pct));
}

export function slotToGeometry(slot, axis) {
  const left = timeToPct(parseTimeToMinutes(slot.start), axis);
  const right = timeToPct(parseTimeToMinutes(slot.end), axis);
  return { leftPct: left, widthPct: Math.max(0, right - left) };
}

// Turn a doctor's raw booked intervals ({ start, end } as "HH:MM") into positioned
// overlay segments. Overlapping/adjacent intervals are merged first so the "dim"
// stays uniform when appointments touch or double-book.
export function buildBusySegments(intervals, axis) {
  const parsed = (intervals || [])
    .map(i => ({ s: parseTimeToMinutes(i.start), e: parseTimeToMinutes(i.end) }))
    .filter(i => i.s != null && i.e != null && i.e > i.s)
    .sort((a, b) => a.s - b.s);
  const merged = [];
  parsed.forEach(i => {
    const last = merged[merged.length - 1];
    if (last && i.s <= last.e) last.e = Math.max(last.e, i.e);
    else merged.push({ ...i });
  });
  return merged.map(m => {
    const left = timeToPct(m.s, axis);
    const right = timeToPct(m.e, axis);
    return { leftPct: left, widthPct: Math.max(0, right - left) };
  });
}

function rangeLabel(slots) {
  return slots
    .map(
      s =>
        `${minutesToLabel(parseTimeToMinutes(s.start))}–${minutesToLabel(
          parseTimeToMinutes(s.end)
        )}`
    )
    .join(", ");
}

export function buildDoctorRows(members, doctors, axis, appointments = []) {
  const byId = new Map();
  (members || []).forEach(m => {
    if (m && m.branchMemberId != null) byId.set(Number(m.branchMemberId), m);
  });
  // Group booked intervals by branchMemberId so each row can dim its taken time.
  const busyByMember = new Map();
  (appointments || []).forEach(a => {
    if (a == null || a.branchMemberId == null) return;
    const key = Number(a.branchMemberId);
    if (!busyByMember.has(key)) busyByMember.set(key, []);
    busyByMember.get(key).push({ start: a.start, end: a.end });
  });
  const rows = [];
  (doctors || []).forEach(doc => {
    const member = byId.get(Number(doc.branchMemberId));
    const day = dayOf(member);
    if (!day) return;
    const slots = day.slots || [];
    if (slots.length > 0) {
      const sorted = [...slots].sort(
        (a, b) => parseTimeToMinutes(a.start) - parseTimeToMinutes(b.start)
      );
      rows.push({
        branchMemberId: Number(doc.branchMemberId),
        name: doc.name,
        color: doc.color || "#5689c9",
        isLeaveWhole: false,
        startMin: parseTimeToMinutes(sorted[0].start),
        ranges: rangeLabel(sorted),
        rooms: sorted.map(s => s.roomId).filter(r => r != null),
        segments: sorted.map(s => ({
          ...slotToGeometry(s, axis),
          start: s.start,
          end: s.end,
          roomId: s.roomId,
        })),
        busySegments: buildBusySegments(
          busyByMember.get(Number(doc.branchMemberId)) || [],
          axis
        ),
      });
      return;
    }
    if ((day.overrideMarkers || []).includes("leave-whole")) {
      rows.push({
        branchMemberId: Number(doc.branchMemberId),
        name: doc.name,
        color: doc.color || "#9aa0a6",
        isLeaveWhole: true,
        startMin: Number.MAX_SAFE_INTEGER, // sorts to the bottom
        ranges: "ลาทั้งวัน",
        rooms: [],
        segments: [],
        busySegments: [],
      });
    }
  });
  rows.sort((a, b) => a.startMin - b.startMin);
  return rows;
}

export function buildCoverageBuckets(members, axis, bucketCount = 16) {
  const span = axis.endMin - axis.startMin;
  const buckets = new Array(bucketCount).fill(0);
  if (span <= 0) return buckets;
  const step = span / bucketCount;
  (members || []).forEach(m => {
    slotsOf(m).forEach(s => {
      const ss = parseTimeToMinutes(s.start);
      const se = parseTimeToMinutes(s.end);
      if (ss == null || se == null) return;
      for (let i = 0; i < bucketCount; i++) {
        const bMid = axis.startMin + i * step + step / 2;
        if (bMid >= ss && bMid < se) buckets[i] += 1;
      }
    });
  });
  return buckets;
}

export function countOnDutyAt(members, now) {
  const nowMin = parseTimeToMinutes(now);
  if (nowMin == null) return 0;
  let count = 0;
  (members || []).forEach(m => {
    const on = slotsOf(m).some(s => {
      const ss = parseTimeToMinutes(s.start);
      const se = parseTimeToMinutes(s.end);
      return ss != null && se != null && nowMin >= ss && nowMin < se;
    });
    if (on) count += 1;
  });
  return count;
}
